// src/pages/analytics/AIAnalytics.jsx
import { useState, useEffect } from 'react';
import {
  MdForum,
  MdPeople,
  MdAttachMoney,
  MdSpeed,
  MdSchool,
  MdCheckCircle,
  MdWarning,
  MdBlock,
  MdEdit,
  MdRestartAlt,
  MdAccountBalanceWallet,
  MdSearch,
  MdClose,
  MdSecurity,
  MdCalculate,
  MdMenuBook,
  MdScience,
  MdBiotech,
  MdEco,
  MdKeyboardArrowDown,
  MdKeyboardArrowUp,
} from 'react-icons/md';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  subscribeToAiUsage,
  subscribeToStudentMastery,
  subscribeToStudentBudgets,
  subscribeToUsersMap,
  updateStudentBudgetLimit,
  resetStudentBudget,
  simulateLiveAiQuestion,
} from '../../services/aiAnalyticsService';
import AnalyticsNavTabs from '../../components/analytics/AnalyticsNavTabs';

/**
 * Resolves a userId or identifier into a clean, human-friendly learner name, subtitle, and initials
 */
const resolveLearnerProfile = (userId, usersMap = {}) => {
  if (!userId) {
    return { name: 'Guest Student', subtitle: 'Anonymous Session', initials: 'GS', isSystem: false };
  }

  // Automated/backend services
  if (userId === 'backend_quiz_gen' || userId.startsWith('system_')) {
    return {
      name: 'Quiz Engine (System)',
      subtitle: 'Automated CBT Generator',
      initials: 'QE',
      isSystem: true,
    };
  }

  // 1. Look up in real-time users map from Firestore
  const user = usersMap[userId];
  if (user) {
    const rawName =
      user.fullName ||
      (user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : (user.firstName || user.lastName)) ||
      user.displayName ||
      user.name;

    const email = user.email || '';
    const classTag = user.classLevel ? ` (${user.classLevel.toUpperCase()})` : '';

    if (rawName && rawName.trim()) {
      const trimmed = rawName.trim();
      const initials = trimmed
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((p) => p[0].toUpperCase())
        .join('');

      return {
        name: `${trimmed}${classTag}`,
        subtitle: email || 'Student Account',
        initials: initials || 'ST',
        isSystem: false,
      };
    }

    if (email) {
      const emailPrefix = email.split('@')[0].replace(/[._-]/g, ' ');
      const formatted = emailPrefix
        .split(' ')
        .filter(Boolean)
        .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
        .join(' ');

      return {
        name: `${formatted}${classTag}`,
        subtitle: email,
        initials: formatted.substring(0, 2).toUpperCase(),
        isSystem: false,
      };
    }
  }

  // 2. Formatted fallback for sample seeded users (e.g. student_kunle_ss1)
  if (userId.startsWith('student_')) {
    const clean = userId.replace(/^student_/, '').replace(/_/g, ' ');
    const parts = clean.split(' ');
    const formatted = parts
      .map((p) => {
        const up = p.toUpperCase();
        return up.startsWith('SS') || up.startsWith('JSS') ? `(${up})` : p.charAt(0).toUpperCase() + p.slice(1);
      })
      .join(' ');

    return {
      name: formatted,
      subtitle: 'Active Student',
      initials: parts[0]?.substring(0, 2).toUpperCase() || 'ST',
      isSystem: false,
    };
  }

  // 3. Fallback for raw Firebase UIDs (e.g. MLdmnA1ajkUrgTCvJY4mUc7ERHy2)
  const shortId = userId.length > 8 ? `${userId.substring(0, 4)}...${userId.substring(userId.length - 4)}` : userId;
  return {
    name: `Learner (${shortId})`,
    subtitle: 'Registered Student',
    initials: userId.substring(0, 2).toUpperCase(),
    isSystem: false,
  };
};

const AIAnalytics = () => {
  const [usageStats, setUsageStats] = useState({
    totalInteractions: 0,
    activeStudents: 0,
    subscribedInteractions: 0,
    freeInteractions: 0,
    estimatedTokens: 0,
    estimatedCost: '0.0000',
    dailyChart: [],
    records: [],
  });

  const [masteryStats, setMasteryStats] = useState({
    averageMastery: 0,
    totalTrackedTopics: 0,
    topicChart: [],
    records: [],
  });

  const [budgetStats, setBudgetStats] = useState({
    records: [],
    totalSpentAllStudents: 0,
    totalTokensAllStudents: 0,
    activeBudgetsCount: 0,
    cappedCount: 0,
    warningCount: 0,
  });

  const [usersMap, setUsersMap] = useState({});
  const [showAllBudgets, setShowAllBudgets] = useState(false);
  const [budgetFilter, setBudgetFilter] = useState('all'); // all, warning, capped, safe
  const [searchTerm, setSearchTerm] = useState('');
  const [editingBudget, setEditingBudget] = useState(null);
  const [newLimitInput, setNewLimitInput] = useState('0.30');
  const [actionMessage, setActionMessage] = useState('');
  const [isSimulating, setIsSimulating] = useState(false);

  useEffect(() => {
    const unsubUsers = subscribeToUsersMap((map) => {
      setUsersMap(map);
    });

    const unsubUsage = subscribeToAiUsage((data) => {
      setUsageStats(data);
    });

    const unsubMastery = subscribeToStudentMastery((data) => {
      setMasteryStats(data);
    });

    const unsubBudgets = subscribeToStudentBudgets((data) => {
      setBudgetStats(data);
    });

    return () => {
      unsubUsers();
      unsubUsage();
      unsubMastery();
      unsubBudgets();
    };
  }, []);

  const pieData = [
    { name: 'Subscribed Students', value: usageStats.subscribedInteractions || 1, color: '#6366F1' },
    { name: 'Free Quota Students', value: usageStats.freeInteractions || 0, color: '#10B981' },
  ];

  const getSubjectMeta = (subjectId) => {
    const s = (subjectId || 'general').toLowerCase();
    if (s.includes('math')) {
      return { name: 'Mathematics', icon: MdCalculate, color: 'text-blue-600', bg: 'bg-blue-50 border-blue-200' };
    }
    if (s.includes('phys')) {
      return { name: 'Physics', icon: MdScience, color: 'text-purple-600', bg: 'bg-purple-50 border-purple-200' };
    }
    if (s.includes('chem')) {
      return { name: 'Chemistry', icon: MdBiotech, color: 'text-amber-600', bg: 'bg-amber-50 border-amber-200' };
    }
    if (s.includes('bio')) {
      return { name: 'Biology', icon: MdEco, color: 'text-emerald-600', bg: 'bg-emerald-50 border-emerald-200' };
    }
    if (s.includes('eng')) {
      return { name: 'English Language', icon: MdMenuBook, color: 'text-teal-600', bg: 'bg-teal-50 border-teal-200' };
    }
    const displayName = s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, ' ');
    return { name: displayName, icon: MdSchool, color: 'text-indigo-600', bg: 'bg-indigo-50 border-indigo-200' };
  };

  const handleOpenEdit = (record) => {
    setEditingBudget(record);
    setNewLimitInput(String(record.budgetLimitUSD || 0.30));
  };

  const handleSaveBudgetLimit = async () => {
    if (!editingBudget) return;
    const val = parseFloat(newLimitInput);
    if (isNaN(val) || val <= 0) {
      alert('Please enter a valid dollar limit (e.g. 0.30 or 0.50)');
      return;
    }
    try {
      await updateStudentBudgetLimit(editingBudget.id, val);
      const learner = resolveLearnerProfile(editingBudget.userId, usersMap);
      setActionMessage(`Updated budget cap for ${learner.name} to $${val.toFixed(2)}`);
      setEditingBudget(null);
      setTimeout(() => setActionMessage(''), 4000);
    } catch (e) {
      alert('Failed to update limit: ' + e.message);
    }
  };

  const handleResetSpend = async (record) => {
    const learner = resolveLearnerProfile(record.userId, usersMap);
    const confirm = window.confirm(
      `Reset spent AI tokens for ${learner.name} in ${getSubjectMeta(record.subjectId).name}? This will restore generative AI access.`
    );
    if (!confirm) return;

    try {
      await resetStudentBudget(record.id);
      setActionMessage(`Reset spent tokens to $0.00 for ${learner.name} (${getSubjectMeta(record.subjectId).name})`);
      setTimeout(() => setActionMessage(''), 4000);
    } catch (e) {
      alert('Failed to reset student budget: ' + e.message);
    }
  };

  const handleSimulateTraffic = async () => {
    setIsSimulating(true);
    try {
      await simulateLiveAiQuestion();
      setActionMessage('Simulated live student AI interaction (+480 tokens, +$0.0018). Live numbers updated in real-time!');
      setTimeout(() => setActionMessage(''), 4000);
    } catch (e) {
      alert('Simulation error: ' + e.message);
    } finally {
      setTimeout(() => setIsSimulating(false), 500);
    }
  };

  const safeCount = budgetStats.records.filter((rec) => !rec.isCapped && !rec.isNearCap).length;

  const filteredBudgets = budgetStats.records.filter((rec) => {
    const term = searchTerm.toLowerCase().trim();
    if (term) {
      const learner = resolveLearnerProfile(rec.userId, usersMap);
      const matchUser =
        (rec.userId || '').toLowerCase().includes(term) ||
        learner.name.toLowerCase().includes(term) ||
        learner.subtitle.toLowerCase().includes(term);
      const matchSub = (rec.subjectId || '').toLowerCase().includes(term);
      const matchMonth = (rec.billingMonth || '').toLowerCase().includes(term);
      if (!matchUser && !matchSub && !matchMonth) return false;
    }

    if (budgetFilter === 'capped') return rec.isCapped;
    if (budgetFilter === 'warning') return rec.isNearCap;
    if (budgetFilter === 'safe') return !rec.isCapped && !rec.isNearCap;
    return true;
  });

  const isSearching = searchTerm.trim().length > 0;
  const displayedBudgets = showAllBudgets || isSearching ? filteredBudgets : filteredBudgets.slice(0, 3);
  const remainingCount = Math.max(0, filteredBudgets.length - 3);

  return (
    <div className="min-h-screen bg-slate-50/60 p-6 lg:p-8 space-y-7 font-sans">
      {/* Navigation Tabs */}
      <AnalyticsNavTabs />

      {/* Top Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-5 border-b border-slate-200">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-poppins tracking-tight">
            AI Cost & Performance Analytics
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">
            Real-time inference telemetry, token consumption, and per-subject budget controls
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200/80">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
            </span>
            Active Telemetry
          </div>

          <button
            onClick={handleSimulateTraffic}
            disabled={isSimulating}
            title="Send a sample interaction to test real-time telemetry updates"
            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-xs font-semibold text-slate-700 bg-white hover:bg-slate-50 hover:text-slate-900 border border-slate-200 shadow-2xs transition-all active:scale-[0.98] disabled:opacity-50"
          >
            {isSimulating ? (
              <>
                <span className="inline-block w-2 h-2 rounded-full bg-indigo-600 animate-ping" />
                <span>Simulating...</span>
              </>
            ) : (
              <span>+ Simulate Traffic</span>
            )}
          </button>
        </div>
      </div>

      {actionMessage && (
        <div className="p-3.5 bg-emerald-500/10 border border-emerald-500/20 text-emerald-800 rounded-2xl text-xs font-semibold flex items-center justify-between shadow-sm animate-fadeIn">
          <div className="flex items-center gap-2">
            <MdCheckCircle className="text-base text-emerald-600" />
            <span>{actionMessage}</span>
          </div>
          <button onClick={() => setActionMessage('')} className="text-emerald-700 hover:text-emerald-950 p-1">
            <MdClose className="text-base" />
          </button>
        </div>
      )}

      {/* 5 Executive KPI Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {/* Total Interactions */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 group">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400 group-hover:text-indigo-600 transition-colors">
              AI Sessions
            </span>
            <div className="p-2.5 bg-indigo-50 text-indigo-600 rounded-xl group-hover:scale-110 transition-transform">
              <MdForum className="text-lg" />
            </div>
          </div>
          <div className="mt-2.5">
            <div className="text-2xl lg:text-3xl font-black text-slate-900 font-poppins tracking-tight">
              {usageStats.totalInteractions.toLocaleString()}
            </div>
            <p className="text-xs text-slate-400 font-medium mt-1">Live questions & lessons</p>
          </div>
        </div>

        {/* Active Students */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 group">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400 group-hover:text-blue-600 transition-colors">
              Active Learners
            </span>
            <div className="p-2.5 bg-blue-50 text-blue-600 rounded-xl group-hover:scale-110 transition-transform">
              <MdPeople className="text-lg" />
            </div>
          </div>
          <div className="mt-2.5">
            <div className="text-2xl lg:text-3xl font-black text-slate-900 font-poppins tracking-tight">
              {usageStats.activeStudents.toLocaleString()}
            </div>
            <p className="text-xs text-slate-400 font-medium mt-1">Engaged with AI Tutor</p>
          </div>
        </div>

        {/* Real Token Consumption */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 group">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400 group-hover:text-amber-600 transition-colors">
              Tokens Consumed
            </span>
            <div className="p-2.5 bg-amber-50 text-amber-600 rounded-xl group-hover:scale-110 transition-transform">
              <MdSpeed className="text-lg" />
            </div>
          </div>
          <div className="mt-2.5">
            <div className="text-2xl lg:text-3xl font-black text-slate-900 font-poppins tracking-tight">
              {(budgetStats.totalTokensAllStudents || usageStats.estimatedTokens).toLocaleString()}
            </div>
            <p className="text-xs text-amber-600 font-semibold mt-1">Prompt & output tokens</p>
          </div>
        </div>

        {/* Actual Gemini Spent Cost */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 group">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400 group-hover:text-emerald-600 transition-colors">
              Total AI Spend
            </span>
            <div className="p-2.5 bg-emerald-50 text-emerald-600 rounded-xl group-hover:scale-110 transition-transform">
              <MdAttachMoney className="text-lg" />
            </div>
          </div>
          <div className="mt-2.5">
            <div className="text-2xl lg:text-3xl font-black text-slate-900 font-poppins tracking-tight">
              ${budgetStats.totalSpentAllStudents > 0 ? budgetStats.totalSpentAllStudents.toFixed(4) : usageStats.estimatedCost}
            </div>
            <p className="text-xs text-emerald-600 font-semibold mt-1">Real Gemini API cost</p>
          </div>
        </div>

        {/* Capped & Warning Budgets */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 group">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400 group-hover:text-purple-600 transition-colors">
              Subject Quotas
            </span>
            <div className="p-2.5 bg-purple-50 text-purple-600 rounded-xl group-hover:scale-110 transition-transform">
              <MdAccountBalanceWallet className="text-lg" />
            </div>
          </div>
          <div className="mt-2.5">
            <div className="text-2xl lg:text-3xl font-black text-slate-900 font-poppins tracking-tight">
              {budgetStats.activeBudgetsCount}
            </div>
            <div className="flex items-center gap-2 mt-1 text-xs">
              <span className="text-rose-600 font-bold">{budgetStats.cappedCount} capped</span>
              <span className="text-slate-300">•</span>
              <span className="text-amber-600 font-bold">{budgetStats.warningCount} near cap</span>
            </div>
          </div>
        </div>
      </div>

      {/* Subject Pricing & Margin */}
      <div className="bg-white rounded-2xl p-6 border border-slate-200/80 shadow-sm">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          {/* Left: Title & Progress Bar */}
          <div className="space-y-3 max-w-xl flex-1">
            <h3 className="text-base font-bold text-slate-900 font-poppins tracking-tight">
              Subject Margin & AI Limits
            </h3>

            {/* Visual Margin Bar */}
            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs font-semibold">
                <span className="text-slate-600">Fee: ₦1,000 / subject</span>
                <span className="text-emerald-600 font-bold">60% (₦600) Margin</span>
              </div>

              {/* Progress bar */}
              <div className="w-full bg-slate-100 rounded-full h-2.5 overflow-hidden flex border border-slate-200/80">
                <div
                  className="bg-emerald-500 h-full rounded-l-full"
                  style={{ width: '60%' }}
                />
                <div
                  className="bg-amber-400 h-full"
                  style={{ width: '25%' }}
                />
                <div
                  className="bg-slate-300 h-full rounded-r-full"
                  style={{ width: '15%' }}
                />
              </div>

              {/* Legend */}
              <div className="flex items-center gap-4 text-xs text-slate-500 pt-0.5">
                <span className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                  <span>Profit (₦600)</span>
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-amber-400"></span>
                  <span>AI Cap ($0.30)</span>
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-slate-400"></span>
                  <span>Hosting</span>
                </span>
              </div>
            </div>
          </div>

          {/* Right: 3 Clean Metrics */}
          <div className="grid grid-cols-3 gap-3 shrink-0 lg:w-[380px]">
            <div className="bg-slate-50 border border-slate-200/80 p-3.5 rounded-xl">
              <div className="text-[11px] text-slate-500 font-medium">Monthly Cap</div>
              <div className="text-base font-bold text-slate-900 mt-0.5 font-poppins">$0.30</div>
            </div>

            <div className="bg-slate-50 border border-slate-200/80 p-3.5 rounded-xl">
              <div className="text-[11px] text-slate-500 font-medium">Alert Level</div>
              <div className="text-base font-bold text-amber-600 mt-0.5 font-poppins">$0.25</div>
            </div>

            <div className="bg-slate-50 border border-slate-200/80 p-3.5 rounded-xl">
              <div className="text-[11px] text-slate-500 font-medium">Quizzes & Tests</div>
              <div className="text-base font-bold text-emerald-600 mt-0.5 font-poppins">Free</div>
            </div>
          </div>
        </div>
      </div>

      {/* STUDENT MONTHLY SUBJECT AI BUDGETS TABLE */}
      <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden">
        <div className="p-5 border-b border-slate-100 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-indigo-50 text-indigo-600 rounded-lg">
                <MdAccountBalanceWallet className="text-lg" />
              </div>
              <h3 className="text-base font-bold text-slate-900 font-poppins tracking-tight">
                Student Subject AI Quotas & Budget Tracking
              </h3>
            </div>
            <p className="text-xs text-slate-500 mt-0.5">
              Live per-subject token spend, $0.25 warning triggers, and $0.30 hard cap enforcement
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {/* Search Input */}
            <div className="relative">
              <MdSearch className="absolute left-3.5 top-2.5 text-slate-400 text-base" />
              <input
                type="text"
                placeholder="Search student or subject..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 pr-3.5 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/40 focus:border-indigo-500 w-48 sm:w-60 font-medium transition-all"
              />
            </div>

            {/* Filter Tabs */}
            <div className="inline-flex rounded-xl border border-slate-200 bg-slate-50 p-1 text-xs">
              <button
                onClick={() => setBudgetFilter('all')}
                className={`px-3 py-1 rounded-lg font-bold transition-all ${
                  budgetFilter === 'all'
                    ? 'bg-white text-indigo-700 shadow-sm'
                    : 'text-slate-500 hover:text-slate-900'
                }`}
              >
                All ({budgetStats.records.length})
              </button>
              <button
                onClick={() => setBudgetFilter('warning')}
                className={`px-3 py-1 rounded-lg font-bold transition-all ${
                  budgetFilter === 'warning'
                    ? 'bg-amber-500 text-white shadow-sm'
                    : 'text-amber-700 hover:text-amber-900'
                }`}
              >
                Near Cap ({budgetStats.warningCount})
              </button>
              <button
                onClick={() => setBudgetFilter('capped')}
                className={`px-3 py-1 rounded-lg font-bold transition-all ${
                  budgetFilter === 'capped'
                    ? 'bg-rose-600 text-white shadow-sm'
                    : 'text-rose-700 hover:text-rose-900'
                }`}
              >
                Capped ({budgetStats.cappedCount})
              </button>
              <button
                onClick={() => setBudgetFilter('safe')}
                className={`px-3 py-1 rounded-lg font-bold transition-all ${
                  budgetFilter === 'safe'
                    ? 'bg-emerald-600 text-white shadow-sm'
                    : 'text-emerald-700 hover:text-emerald-900'
                }`}
              >
                Safe ({safeCount})
              </button>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse text-sm">
            <thead>
              <tr className="bg-slate-50/75 border-b border-slate-100 text-slate-400 font-bold text-[11px] uppercase tracking-wider">
                <th className="px-5 py-3.5">Learner Account</th>
                <th className="px-5 py-3.5">Subject</th>
                <th className="px-5 py-3.5">Billing Month</th>
                <th className="px-5 py-3.5">Spent / Budget</th>
                <th className="px-5 py-3.5">Quota Consumption</th>
                <th className="px-5 py-3.5">Tokens Burned</th>
                <th className="px-5 py-3.5">Status</th>
                <th className="px-5 py-3.5 text-right">Admin Controls</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredBudgets.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-5 py-12 text-center text-slate-400">
                    <div className="flex flex-col items-center justify-center">
                      <div className="p-3 bg-slate-100 rounded-2xl mb-2 text-slate-400">
                        <MdAccountBalanceWallet className="text-2xl" />
                      </div>
                      <p className="text-sm font-semibold text-slate-700">No student budget records matching filters.</p>
                      <p className="text-xs text-slate-400 mt-0.5">
                        Budgets populate automatically in real time as students interact with AI in their subjects.
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                displayedBudgets.map((record) => {
                  const spent = Number(record.totalSpentUSD || 0);
                  const limit = Number(record.budgetLimitUSD || 0.30);
                  const pct = Math.min(100, Math.round((spent / limit) * 100));
                  const meta = getSubjectMeta(record.subjectId);
                  const SubjectIcon = meta.icon;

                  const learner = resolveLearnerProfile(record.userId, usersMap);

                  return (
                    <tr key={record.id} className="hover:bg-slate-50/80 transition-colors">
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-2.5">
                          <div
                            className={`w-8 h-8 rounded-xl font-bold text-xs flex items-center justify-center shadow-sm shrink-0 ${
                              learner.isSystem
                                ? 'bg-slate-800 text-slate-200'
                                : 'bg-gradient-to-br from-indigo-500 to-purple-600 text-white'
                            }`}
                          >
                            {learner.initials}
                          </div>
                          <div className="min-w-0 max-w-[220px]">
                            <div className="flex items-center gap-1.5">
                              <span className="font-semibold text-xs text-slate-900 truncate" title={learner.name}>
                                {learner.name}
                              </span>
                              {learner.isSystem && (
                                <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-slate-100 text-slate-600 border border-slate-200 shrink-0">
                                  System
                                </span>
                              )}
                            </div>
                            <div className="text-[11px] text-slate-400 truncate flex items-center gap-1.5" title={learner.subtitle}>
                              <span className="truncate">{learner.subtitle}</span>
                              <span>•</span>
                              <span className="shrink-0">{record.interactionCount || 0} chats</span>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-3.5">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-xl text-xs font-bold border ${meta.bg} ${meta.color}`}>
                          <SubjectIcon className="text-sm" />
                          {meta.name}
                        </span>
                      </td>
                      <td className="px-5 py-3.5 text-slate-500 text-xs font-mono font-medium">
                        {record.billingMonth || 'Current'}
                      </td>
                      <td className="px-5 py-3.5">
                        <span className="font-extrabold text-slate-900 font-poppins">${spent.toFixed(4)}</span>{' '}
                        <span className="text-xs text-slate-400 font-normal">/ ${limit.toFixed(2)}</span>
                      </td>
                      <td className="px-5 py-3.5 min-w-[140px]">
                        <div className="flex items-center gap-2.5">
                          <div className="flex-1 bg-slate-100 rounded-full h-2 overflow-hidden">
                            <div
                              className={`h-full rounded-full transition-all duration-500 ${
                                record.isCapped
                                  ? 'bg-gradient-to-r from-rose-500 to-red-600'
                                  : record.isNearCap
                                  ? 'bg-gradient-to-r from-amber-400 to-amber-500'
                                  : 'bg-gradient-to-r from-emerald-400 to-teal-500'
                              }`}
                              style={{ width: `${pct}%` }}
                            ></div>
                          </div>
                          <span
                            className={`text-xs font-black font-poppins ${
                              record.isCapped
                                ? 'text-rose-600'
                                : record.isNearCap
                                ? 'text-amber-600'
                                : 'text-emerald-600'
                            }`}
                          >
                            {pct}%
                          </span>
                        </div>
                      </td>
                      <td className="px-5 py-3.5 text-xs">
                        <div className="font-bold text-slate-900">{(record.totalTokens || 0).toLocaleString()}</div>
                        <div className="text-[10px] text-slate-400">
                          in: {(record.promptTokens || 0).toLocaleString()} • out: {(record.candidateTokens || 0).toLocaleString()}
                        </div>
                      </td>
                      <td className="px-5 py-3.5">
                        {record.isCapped ? (
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-extrabold bg-rose-50 text-rose-700 border border-rose-200">
                            <MdBlock className="mr-1 text-sm text-rose-600" />
                            Capped ($0.30)
                          </span>
                        ) : record.isNearCap ? (
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-extrabold bg-amber-50 text-amber-700 border border-amber-200">
                            <MdWarning className="mr-1 text-sm text-amber-600" />
                            Near Cap (≥ $0.25)
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-extrabold bg-emerald-50 text-emerald-700 border border-emerald-200">
                            <MdCheckCircle className="mr-1 text-sm text-emerald-600" />
                            Active
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-3.5 text-right space-x-1.5">
                        <button
                          onClick={() => handleOpenEdit(record)}
                          title="Adjust student budget cap"
                          className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-bold bg-slate-100 text-slate-700 hover:bg-indigo-50 hover:text-indigo-600 border border-slate-200 transition-colors shadow-sm"
                        >
                          <MdEdit className="mr-1" />
                          Adjust
                        </button>
                        <button
                          onClick={() => handleResetSpend(record)}
                          title="Reset student monthly spend to $0.00"
                          className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-bold bg-slate-100 text-rose-700 hover:bg-rose-50 hover:text-rose-800 border border-slate-200 transition-colors shadow-sm"
                        >
                          <MdRestartAlt className="mr-1" />
                          Reset
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* View All / Show Less Footer */}
        {!isSearching && filteredBudgets.length > 3 && (
          <div className="p-3.5 border-t border-slate-100 bg-slate-50/60 flex items-center justify-between">
            <span className="text-xs text-slate-500 font-medium">
              Showing {displayedBudgets.length} of {filteredBudgets.length} learner accounts
            </span>
            <button
              onClick={() => setShowAllBudgets((prev) => !prev)}
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-xs font-semibold text-indigo-600 hover:text-indigo-700 bg-indigo-50/80 hover:bg-indigo-100/80 border border-indigo-200/60 shadow-2xs transition-all active:scale-[0.98]"
            >
              {showAllBudgets ? (
                <>
                  <MdKeyboardArrowUp className="text-base" />
                  <span>Show Less (Top 3)</span>
                </>
              ) : (
                <>
                  <MdKeyboardArrowDown className="text-base" />
                  <span>View All (+{remainingCount} more)</span>
                </>
              )}
            </button>
          </div>
        )}
      </div>

      {/* Analytics Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Daily Activity Chart */}
        <div className="lg:col-span-2 bg-white p-6 rounded-2xl border border-slate-200/80 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-base font-bold text-slate-900 font-poppins tracking-tight">Daily AI Teacher Interactions</h3>
              <p className="text-xs text-slate-400">Student queries, analogies, and practice requests</p>
            </div>
            <span className="px-2.5 py-1 rounded-lg text-xs font-bold bg-indigo-50 text-indigo-700 border border-indigo-100">
              Last 7 Days
            </span>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={usageStats.dailyChart} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="aiActivity" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#6366F1" stopOpacity={0.35} />
                    <stop offset="95%" stopColor="#6366F1" stopOpacity={0.0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F5F9" />
                <XAxis dataKey="date" stroke="#94A3B8" fontSize={12} tickLine={false} />
                <YAxis stroke="#94A3B8" fontSize={12} tickLine={false} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#0F172A',
                    border: '1px solid #334155',
                    borderRadius: '12px',
                    color: '#fff',
                    fontSize: '12px',
                    boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
                  }}
                />
                <Area type="monotone" dataKey="requests" stroke="#6366F1" strokeWidth={3} fill="url(#aiActivity)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Student Tier Breakdown */}
        <div className="bg-white p-6 rounded-2xl border border-slate-200/80 shadow-sm flex flex-col justify-between">
          <div>
            <h3 className="text-base font-bold text-slate-900 font-poppins tracking-tight">User Quota Distribution</h3>
            <p className="text-xs text-slate-400">Subscribed ($0.30/subj) vs Free tier</p>
          </div>
          <div className="h-52 my-auto">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={52}
                  outerRadius={78}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex justify-around text-xs border-t border-slate-100 pt-3.5">
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-indigo-500"></span>
              <span className="text-slate-600 font-bold">Subscribed</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-emerald-500"></span>
              <span className="text-slate-600 font-bold">Free Tier</span>
            </div>
          </div>
        </div>
      </div>

      {/* Topic Mastery Performance Bar Chart */}
      <div className="bg-white p-6 rounded-2xl border border-slate-200/80 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-base font-bold text-slate-900 font-poppins tracking-tight">
              Curriculum Mastery Telemetry
            </h3>
            <p className="text-xs text-slate-400">Average student mastery score percentage per curriculum topic</p>
          </div>
          <span className="px-2.5 py-1 rounded-lg text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-100">
            Avg: {masteryStats.averageMastery}%
          </span>
        </div>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={
                masteryStats.topicChart.length > 0
                  ? masteryStats.topicChart
                  : [
                      { name: 'Number Bases', avgMastery: 72, students: 1 },
                      { name: 'Modular Arith', avgMastery: 65, students: 1 },
                      { name: 'Indices', avgMastery: 80, students: 1 },
                      { name: 'Logarithms', avgMastery: 58, students: 1 },
                      { name: 'Sets & Venn', avgMastery: 75, students: 1 },
                    ]
              }
              margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F5F9" />
              <XAxis dataKey="name" stroke="#94A3B8" fontSize={12} tickLine={false} />
              <YAxis stroke="#94A3B8" fontSize={12} tickLine={false} domain={[0, 100]} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#0F172A',
                  border: '1px solid #334155',
                  borderRadius: '12px',
                  color: '#fff',
                  fontSize: '12px',
                }}
              />
              <Bar dataKey="avgMastery" fill="#6366F1" radius={[8, 8, 0, 0]} name="Average Mastery %" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* EDIT BUDGET MODAL */}
      {editingBudget && (
        <div className="fixed inset-0 bg-slate-950/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fadeIn">
          <div className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl space-y-4 border border-slate-100">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3.5">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-indigo-50 text-indigo-600 rounded-xl">
                  <MdSecurity className="text-lg" />
                </div>
                <h4 className="text-base font-bold text-slate-900 font-poppins">Adjust Student AI Budget Cap</h4>
              </div>
              <button
                onClick={() => setEditingBudget(null)}
                className="text-slate-400 hover:text-slate-700 p-1.5 rounded-xl hover:bg-slate-100 transition-colors"
              >
                <MdClose className="text-lg" />
              </button>
            </div>

            <div className="space-y-3 text-xs">
              <div className="flex justify-between p-2.5 bg-slate-50 rounded-xl">
                <span className="text-slate-500 font-medium">Learner Name:</span>
                <span className="font-bold text-slate-900">{resolveLearnerProfile(editingBudget.userId, usersMap).name}</span>
              </div>
              <div className="flex justify-between p-2.5 bg-slate-50 rounded-xl">
                <span className="text-slate-500 font-medium">Subject:</span>
                <span className="font-bold text-indigo-600">{getSubjectMeta(editingBudget.subjectId).name}</span>
              </div>
              <div className="flex justify-between p-2.5 bg-slate-50 rounded-xl">
                <span className="text-slate-500 font-medium">Current Month Spend:</span>
                <span className="font-black text-slate-900 font-poppins">${Number(editingBudget.totalSpentUSD || 0).toFixed(4)}</span>
              </div>

              <div className="pt-2">
                <label className="block text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                  New Monthly AI Budget Limit (USD)
                </label>
                <div className="relative">
                  <span className="absolute left-3.5 top-2.5 text-slate-400 font-bold">$</span>
                  <input
                    type="number"
                    step="0.05"
                    min="0.10"
                    max="10.00"
                    value={newLimitInput}
                    onChange={(e) => setNewLimitInput(e.target.value)}
                    className="w-full pl-8 pr-3.5 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 focus:outline-none font-bold text-slate-900 text-sm"
                    placeholder="0.30"
                  />
                </div>
                <p className="text-[11px] text-slate-400 mt-1">
                  Default budget is $0.30. You can increase this to grant extra AI tokens to individual learners.
                </p>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2.5 pt-3 border-t border-slate-100">
              <button
                onClick={() => setEditingBudget(null)}
                className="px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-100 rounded-xl transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveBudgetLimit}
                className="px-5 py-2 text-xs font-bold text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 rounded-xl shadow-md shadow-indigo-500/20 transition-all hover:scale-[1.02]"
              >
                Save Budget Cap
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIAnalytics;
